import React from 'react';
import styled from 'styled-components';
import colors from '../../theme/colors';

const Minimap = styled.div`
  position: absolute;
  top: 20px;
  right: ${props => props.$collapsed ? `${-props.width + 20}px` : '7px'};
  width: ${props => props.width}px;
  height: 80px;
  background: ${colors.base.themePrimary};
  border: 1px solid ${colors.base.themeSecondaryTransparent};
  border-radius: 30px;
  z-index: 1000;
  transition: all 0.3s ease;

  /* Bring minimap + its handle above other overlays when last clicked */
  &:focus-within {
    z-index: 1200;
  }

  /* Mobile: slightly tighter margins */
  @media (max-width: 768px), (max-height: 768px) {
    top: 10px;
    right: ${props => props.$collapsed ? `${-props.width + 10}px` : '2px'};
  }
`;

const MinimapContent = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 30px;
`;

const MinimapNode = styled.div`
  position: absolute;
  width: 4px;
  height: 4px;
  background: ${props => props.$isSelected ? colors.ui.premiumBadge : colors.text.white70};
  border-radius: 50%;
  transform: translate(-50%, -50%);
`;

const MinimapViewport = styled.div`
  position: absolute;
  border: 1px solid ${colors.base.themePrimaryVariant};
  background: ${colors.ui.minimap};
  pointer-events: none;
  border: 1px solid ${colors.base.themeSecondaryTransparent};
`;

const MinimapToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: -52px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background: ${colors.primary.darkRgba};
  border: 1px solid ${colors.button.aboutBorder}; /* light contour like Read Me */
  border-radius: 50%;
  color: white;
  cursor: pointer;
  padding: 0;
  transition: all 0.2s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  svg {
    width: 18px;
    height: 18px;
    stroke: rgba(255, 255, 255, 0.95);
    stroke-width: 2.6;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
  }

  @media (max-width: 768px), (max-height: 768px) {
    left: -30px;
    width: 26px;
    height: 26px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }

  &:hover {
    background: ${colors.button.aboutBgHover};
    border-color: ${colors.border.lightHover};
    transform: translateY(-50%) scale(1.15);
    box-shadow: ${colors.effects.shadowLarge};
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const MinimapComponent = ({
  nodePositions,
  camera,
  selectedNodeId,
  collapsed,
  containerRect,
  onToggle,
  onNodeClick,
  isMobile
}) => {
  if (!nodePositions || nodePositions.size === 0 || !containerRect) {
    return null;
  }

  // Calculate actual bounds from ALL node positions (not just visible)
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

  nodePositions.forEach((pos) => {
    minX = Math.min(minX, pos.x);
    maxX = Math.max(maxX, pos.x);
    minY = Math.min(minY, pos.y);
    maxY = Math.max(maxY, pos.y);
  });

  // Add padding to bounds
  const padding = 500;
  minX -= padding;
  maxX += padding;
  minY -= padding;
  maxY += padding;

  const worldWidth = maxX - minX;
  const worldHeight = maxY - minY;

  // Calculate minimap dimensions based on aspect ratio
  const minimapHeight = 80;
  const aspectRatio = worldWidth / worldHeight;

  // Calculate width from aspect ratio, with min/max constraints
  const minWidth = 50;
  const maxWidth = 600;
  const calculatedWidth = minimapHeight * aspectRatio;
  const minimapWidth = Math.round(Math.max(minWidth, Math.min(maxWidth, calculatedWidth)));

  // Calculate scale to fit in minimap
  const scaleX = minimapWidth / worldWidth;
  const scaleY = minimapHeight / worldHeight;
  const minimapScale = Math.min(scaleX, scaleY);

  // Calculate actual content dimensions in minimap
  const contentWidth = worldWidth * minimapScale;
  const contentHeight = worldHeight * minimapScale;

  // Center content if it's smaller than minimap container
  const offsetX = (minimapWidth - contentWidth) / 2;
  const offsetY = (minimapHeight - contentHeight) / 2;

  // Calculate viewport rectangle in minimap
  const viewportWidth = (containerRect.width / camera.scale) * minimapScale;
  const viewportHeight = (containerRect.height / camera.scale) * minimapScale;
  const viewportX = ((-camera.x / camera.scale) - minX) * minimapScale + offsetX;
  const viewportY = ((-camera.y / camera.scale) - minY) * minimapScale + offsetY;

  return (
    <Minimap $collapsed={collapsed} width={minimapWidth}>
      <MinimapToggle
        onClick={onToggle}
        title={collapsed ? "Show minimap" : "Hide minimap"}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ transform: `rotate(${collapsed ? 90 : -90}deg)` }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </MinimapToggle>
      <MinimapContent>
        {Array.from(nodePositions.entries()).map(([nodeId, pos]) => (
          <MinimapNode
            key={nodeId}
            $isSelected={nodeId === selectedNodeId}
            style={{
              left: `${(pos.x - minX) * minimapScale + offsetX}px`,
              top: `${(pos.y - minY) * minimapScale + offsetY}px`
            }}
            onClick={() => onNodeClick(nodeId)}
          />
        ))}
        <MinimapViewport
          style={{
            left: `${viewportX}px`,
            top: `${viewportY}px`,
            width: `${viewportWidth}px`,
            height: `${viewportHeight}px`
          }}
        />
      </MinimapContent>
    </Minimap>
  );
};

export default MinimapComponent;
